import { useCallback, useEffect, useRef, useState } from 'react';

import { downloadExport, type ExportDownload } from '../../api/downloadExport';

export interface UseExportDownloadModalArgs {
  onClose: () => void;
  download: ExportDownload;
}

export interface UseExportDownloadModalResult {
  isSubmitting: boolean;
  submittingError: { message: string } | null;
  /** Manually re-fire the download. Wired to the error-state Retry button. */
  onRetry: () => void;
}

export const useExportDownloadModal = ({
  onClose,
  download,
}: UseExportDownloadModalArgs): UseExportDownloadModalResult => {
  // Primitives, not the object, drive the effect — a caller building `download`
  // inline must not re-fire the one-shot download on every render.
  const { key, path, filename } = download;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingError, setSubmittingError] = useState<Error | null>(null);
  // Bumping this re-runs the download effect — wired to the error-state Retry.
  const [attempt, setAttempt] = useState(0);

  // Keep `onClose` current without making it a download-effect dependency, so a
  // parent re-render can't re-fire the one-shot download.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Fire the download from the effect rather than a react-query mutation. Under
  // StrictMode's mount -> unmount -> mount, a mutation observer detaches from its
  // in-flight mutation on the throwaway unmount and never re-attaches
  // (MutationObserver.onUnsubscribe removes the observer; onSubscribe is a no-op) —
  // so the file downloads but neither the observer's `isSuccess` state nor its
  // `onSuccess` callback ever fires, leaving the modal stuck open. The api layer
  // shares one in-flight request per download key, so the remount joins the same fetch.
  //
  // The fetch is intentionally NEVER aborted: closing the modal only stops this
  // hook from touching state (`disposed`) — the download keeps running in the
  // background and the file still saves when it resolves.
  useEffect(() => {
    let disposed = false;
    setIsSubmitting(true);
    setSubmittingError(null);

    downloadExport({ key, path, filename })
      .then(() => {
        if (!disposed) onCloseRef.current();
      })
      .catch((err: unknown) => {
        if (!disposed)
          setSubmittingError(err instanceof Error ? err : new Error('Download failed'));
      })
      .finally(() => {
        if (!disposed) setIsSubmitting(false);
      });

    return () => {
      disposed = true;
    };
  }, [key, path, filename, attempt]);

  const onRetry = useCallback(() => setAttempt((n) => n + 1), []);

  return { isSubmitting, submittingError, onRetry };
};
